import type { Filters } from './types';
export { SQ_METRES_PER_ACRE, SQ_FEET_PER_ACRE, FEET_PER_METRE } from './units';
export { NOISE, EVIDENCE_LABELS, STATUS_LABELS, CATS, CATALOG, CATALOG_BY_ID, PENS, SIZES, TOOLS } from './catalogue';
export { DEFAULT_FILTERS, formatPrice, formatAcres } from './markets';

export const MALAYSIA_STATES = [
  'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 'Pahang', 'Perak',
  'Perlis', 'Pulau Pinang', 'Sabah', 'Sarawak', 'Selangor',
  'Terengganu', 'W.P. Kuala Lumpur', 'W.P. Labuan', 'W.P. Putrajaya',
] as const;
export type MalaysiaState = (typeof MALAYSIA_STATES)[number];
// ISO 3166-2:MY numbering: MY-01 is Johor through MY-16 Putrajaya.
const STATES_BY_ISO_NUMBER: readonly MalaysiaState[] = [
  'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 'Pahang', 'Pulau Pinang', 'Perak',
  'Perlis', 'Selangor', 'Terengganu', 'Sabah', 'Sarawak', 'W.P. Kuala Lumpur', 'W.P. Labuan', 'W.P. Putrajaya',
];
const STATE_ALIASES: Readonly<Record<string, MalaysiaState>> = {
  johor: 'Johor', johore: 'Johor', kedah: 'Kedah', kelantan: 'Kelantan', melaka: 'Melaka', malacca: 'Melaka',
  'negeri sembilan': 'Negeri Sembilan', pahang: 'Pahang', perak: 'Perak', perlis: 'Perlis',
  'pulau pinang': 'Pulau Pinang', penang: 'Pulau Pinang', sabah: 'Sabah', sarawak: 'Sarawak', selangor: 'Selangor',
  terengganu: 'Terengganu', 'kuala lumpur': 'W.P. Kuala Lumpur', labuan: 'W.P. Labuan', putrajaya: 'W.P. Putrajaya',
};
/** Maps portal state labels ("Johor Darul Ta'zim", "Penang", "MY-10") to the canonical state name. */
export function malaysianState(value: string): MalaysiaState | null {
  const code = /^MY-(\d{2})$/i.exec(value.trim());
  if (code) return STATES_BY_ISO_NUMBER[Number(code[1]) - 1] ?? null;
  const name = value.toLowerCase()
    .replace(/\s+(?:darul|indera|bandaraya)\b.*$/, '')
    .replace(/^(?:w\.?\s*p\.?|wilayah persekutuan|federal territory(?: of)?)\s+/, '')
    .replace(/[^a-z ]/g, ' ').replace(/\s+/g, ' ').trim();
  return STATE_ALIASES[name] ?? null;
}
export const MALAYSIA = {
  code: 'MY', name: 'Malaysia', currency: 'MYR', currencySymbol: 'RM',
  locale: 'en-MY', timezone: 'Asia/Kuala_Lumpur', mapCentre: [4.3, 108.5] as const,
  states: MALAYSIA_STATES,
  defaultSearch: "Farmland with room for a house, fruit trees and a pond, under RM900k",
};
/** Historical defaults; active application defaults now live in markets.ts. */
export const MALAYSIA_DEFAULT_FILTERS: Filters = {
  country: 'MY', currency: 'MYR', maxPrice: null, maxPricePerAcre: null, minAcres: 1,
  home: 'nice', ownership: 'any', titleRestriction: 'any', walking: 'any', internet: 'any',
  noise: [], plants: [], weights: {}, requirements: [], location: '',
};
